import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, makeStyles } from '@material-ui/core';
import Image from '@material-ui/icons/Image';
import TextFields from '@material-ui/icons/TextFields';
import { addEditorItem, setEditorSelectedId } from '../../Reducers/Configurator/Actions';
import {
  getDefaultCanvasSize,
  getDefaultColorHex,
  getDefaultColorOptionId,
  getEditorObjects,
  getFonts,
  getOptionsForClassification,
  getSelectedOptionForClassification,
  OptionClassificationIdentifiers,
} from '../../Reducers/Configurator/Selectors';
import { SurfaceOptionIdentifiers } from '../../Enums';
import { attrIsOn } from '../../utils/optionAttributes';
import { DEFAULT_LOCKED_DIMENSION, DEFAULT_LOCKED_WIDTH_CM } from '../../constants/editorDefaults';
import { XXL_COLORS } from '../../theme/colors';
import { cmToMm, DEFAULT_CANVAS_HEIGHT_MM, DEFAULT_CANVAS_WIDTH_MM } from '../../utils/editorUnits';
import { computeSizeFromLockedWidth } from '../../utils/itemSizing';
import {
  DEFAULT_TEXT_BASE_HEIGHT,
  DEFAULT_TEXT_BASE_WIDTH,
  PATH_UNIT_TO_MM,
} from '../../utils/textMetrics';
import { getDefaultFontRecord } from '../../utils/fonts';
import { computeDesignSize } from '../../utils/designSize';
import type { EditorItem, ImageItem, TextItem } from '../../Reducers/Configurator/types';
import { t } from '@configuratorware/configurator-frontendgui/Framework/i18n';

const SPAWN_GAP_MM = 20;

function computeSpawnPosition(
  objects: Record<string, EditorItem>,
  width: number,
  height: number,
  canvasW: number,
  canvasH: number,
): { x: number; y: number } {
  const centered = { x: canvasW / 2 - width / 2, y: canvasH / 2 - height / 2 };
  if (Object.keys(objects).length === 0) return centered;

  const bounds = computeDesignSize(objects);
  const x = bounds.x + bounds.width / 2 - width / 2;
  const below = bounds.y + bounds.height + SPAWN_GAP_MM;
  if (below + height <= canvasH) return { x, y: below };

  const above = bounds.y - SPAWN_GAP_MM - height;
  if (above >= 0) return { x, y: above };

  return centered;
}

function generateId() {
  return `item-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
}

const useStyles = makeStyles((theme) => {
  const tokens = (theme as { xxlprint?: typeof XXL_COLORS }).xxlprint || XXL_COLORS;
  return {
    bar: {
      position: 'sticky',
      top: 0,
      zIndex: 10,
      backgroundColor: tokens.paperBackground,
      marginLeft: -theme.spacing(2),
      marginRight: -theme.spacing(2),
      padding: theme.spacing(1.5, 2),
      borderBottom: `1px solid ${tokens.borderStrong}`,
      display: 'flex',
      gap: theme.spacing(1),
      // Opaque cap that fills the scroll container's top padding so content
      // scrolling underneath never peeks above the pinned bar.
      '&::before': {
        content: '""',
        position: 'absolute',
        left: 0,
        right: 0,
        bottom: '100%',
        height: theme.spacing(2),
        backgroundColor: tokens.paperBackground,
      },
    },
    button: {
      flex: 1,
      textTransform: 'uppercase',
      fontWeight: 700,
      letterSpacing: '0.04em',
      fontSize: 13,
      color: tokens.sectionTitle,
      borderColor: tokens.borderStrong,
      boxShadow: 'none',
      '&:hover': {
        backgroundColor: tokens.tileSelectedBg,
        borderColor: tokens.accent,
        boxShadow: 'none',
      },
    },
  };
});

export default function DesignSection() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const objects = useSelector(getEditorObjects);
  const fonts = useSelector(getFonts);
  const defaultColor = useSelector(getDefaultColorHex);
  const defaultColorOptionId = useSelector(getDefaultColorOptionId);
  const canvasSize = useSelector(getDefaultCanvasSize);
  const materialOptions = useSelector(getOptionsForClassification(OptionClassificationIdentifiers.material));
  const materialSelected = useSelector(
    getSelectedOptionForClassification(OptionClassificationIdentifiers.material)
  );

  const materialIsGlossOnly = React.useMemo(() => {
    const id = materialSelected?.identifier;
    if (!id) return false;
    const opt = materialOptions.find((o) => o.identifier === id);
    return opt ? attrIsOn(opt, 'glossOnly') : false;
  }, [materialOptions, materialSelected?.identifier]);

  const initialSelectedOptions = React.useMemo(() => {
    const previousSurface = Object.values(objects).find(
      (item) => item.selectedOptions?.[OptionClassificationIdentifiers.surface]
    )?.selectedOptions?.[OptionClassificationIdentifiers.surface];
    const defaultSurface = materialIsGlossOnly
      ? SurfaceOptionIdentifiers.gloss
      : SurfaceOptionIdentifiers.matte;
    return {
      [OptionClassificationIdentifiers.surface]: previousSurface ?? defaultSurface,
    };
  }, [objects, materialIsGlossOnly]);

  const handleAddText = () => {
    const id = generateId();
    const viewBoxWidth = DEFAULT_TEXT_BASE_WIDTH;
    const viewBoxHeight = DEFAULT_TEXT_BASE_HEIGHT;
    const baseWidth = viewBoxWidth * PATH_UNIT_TO_MM;
    const baseHeight = viewBoxHeight * PATH_UNIT_TO_MM;
    const defaultWidth = cmToMm(DEFAULT_LOCKED_WIDTH_CM);
    const canvasW = canvasSize.width || DEFAULT_CANVAS_WIDTH_MM;
    const canvasH = canvasSize.height || DEFAULT_CANVAS_HEIGHT_MM;
    const { width, height, scale } = computeSizeFromLockedWidth(
      baseWidth,
      baseHeight,
      defaultWidth,
      canvasH
    );
    const spawn = computeSpawnPosition(objects, width, height, canvasW, canvasH);

    const item: TextItem = {
      type: 'text',
      id,
      content: '',
      fontFamily: getDefaultFontRecord(fonts)?.name || 'Roboto',
      color: defaultColor,
      ...(defaultColorOptionId ? { colorOptionId: defaultColorOptionId } : {}),
      selectedOptions: { ...initialSelectedOptions },
      x: spawn.x,
      y: spawn.y,
      width,
      height,
      baseWidth,
      baseHeight,
      trimViewBoxX: 0,
      trimViewBoxY: 0,
      viewBoxWidth,
      viewBoxHeight,
      scale,
      lockedDimension: DEFAULT_LOCKED_DIMENSION,
    };
    dispatch(addEditorItem(item));
    dispatch(setEditorSelectedId(id));
  };

  const handleAddLogo = () => {
    const id = generateId();
    const defaultWidth = cmToMm(DEFAULT_LOCKED_WIDTH_CM);
    const canvasW = canvasSize.width || DEFAULT_CANVAS_WIDTH_MM;
    const canvasH = canvasSize.height || DEFAULT_CANVAS_HEIGHT_MM;
    const spawn = computeSpawnPosition(objects, defaultWidth, defaultWidth, canvasW, canvasH);

    const item: ImageItem = {
      type: 'image',
      id,
      url: '',
      svgSource: '',
      colorAreas: [],
      colorAreaBounds: [],
      selectedOptions: { ...initialSelectedOptions },
      x: spawn.x,
      y: spawn.y,
      width: defaultWidth,
      height: defaultWidth,
      baseWidth: defaultWidth,
      baseHeight: defaultWidth,
      viewBoxWidth: 100,
      viewBoxHeight: 100,
      scale: 1,
      lockedDimension: DEFAULT_LOCKED_DIMENSION,
    };
    dispatch(addEditorItem(item));
    dispatch(setEditorSelectedId(id));
  };

  return (
    <div className={classes.bar}>
      <Button
        variant="outlined"
        className={classes.button}
        startIcon={<TextFields fontSize="small" />}
        onClick={handleAddText}
      >
        {t('addVisualElement.text')}
      </Button>
      <Button
        variant="outlined"
        className={classes.button}
        startIcon={<Image fontSize="small" />}
        onClick={handleAddLogo}
      >
        {t('addVisualElement.logo')}
      </Button>
    </div>
  );
}
